import {
  Badge, Button, Card, Group, Progress, Skeleton, Stack, Text, UnstyledButton,
} from '@mantine/core';
import { IconArrowRight } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { brandBadgeStyle } from '@/utils/color';
import { badgeStyles } from '@/utils/tableStyles';
import { ATTENTION_LIST_SIZE } from '../constants';

/**
 * MyListCard — a short list of the current user's own work.
 *
 * The Dashboard equivalent of the Overview's `CriticalListCard`, kept separate because
 * the shapes differ: rows here carry a progress figure rather than a total-count header,
 * and there is no org-wide "+N more" — the "View all" route is always the user's own list
 * page, never an admin one.
 */
export interface MyListRow {
  id: number | string;
  title: string;
  meta?: string;
  badge?: string;
  badgeColor?: string | null;
  /** 0-100; renders a thin progress track under the row when present. */
  progress?: number;
  to?: string;
}

export interface MyListCardProps {
  title: string;
  subtitle?: string;
  rows: MyListRow[];
  loading?: boolean;
  viewAllTo?: string;
  emptyMessage?: string;
  limit?: number;
}

export default function MyListCard({
  title,
  subtitle,
  rows,
  loading = false,
  viewAllTo,
  emptyMessage = 'Nothing here yet.',
  limit = ATTENTION_LIST_SIZE,
}: MyListCardProps) {
  const navigate = useNavigate();
  const shown = rows.slice(0, limit);
  const more = rows.length - shown.length;

  return (
    <Card withBorder radius="md" padding="lg" h="100%">
      <Group justify="space-between" align="flex-start" mb="md" wrap="nowrap">
        <div>
          <Text fw={600}>{title}</Text>
          {subtitle && <Text size="sm" c="dimmed">{subtitle}</Text>}
        </div>
        {viewAllTo && (
          <Button
            variant="subtle"
            size="compact-xs"
            rightSection={<IconArrowRight size={13} />}
            onClick={() => navigate(viewAllTo)}
          >
            View all
          </Button>
        )}
      </Group>

      {loading && rows.length === 0 ? (
        <Stack gap="xs">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} height={40} radius="sm" />
          ))}
        </Stack>
      ) : shown.length === 0 ? (
        <Text size="sm" c="dimmed">{emptyMessage}</Text>
      ) : (
        <Stack gap={4}>
          {shown.map((row) => (
            <UnstyledButton
              key={row.id}
              onClick={() => row.to && navigate(row.to)}
              style={{ borderRadius: 6 }}
            >
              <Stack gap={4} py={6} px={4}>
                <Group justify="space-between" wrap="nowrap">
                  <div style={{ minWidth: 0 }}>
                    <Text size="sm" fw={500} truncate>{row.title}</Text>
                    {row.meta && <Text size="xs" c="dimmed" truncate>{row.meta}</Text>}
                  </div>
                  {row.badge && (
                    <Badge
                      size="sm"
                      variant="filled"
                      style={brandBadgeStyle(row.badgeColor)}
                      styles={badgeStyles}
                    >
                      {row.badge}
                    </Badge>
                  )}
                </Group>

                {/*
                  A labelled bar, not a bare 4px hairline. Progress is the point of this row for a
                  facilitator, and an unlabelled sliver of primary on a dark surface reads as
                  decoration. Mantine's `Progress` carries its own contrast-aware track.
                */}
                {row.progress !== undefined && (
                  <Group gap="xs" wrap="nowrap">
                    <Progress
                      value={Math.min(100, Math.max(0, row.progress))}
                      size="md"
                      radius="xl"
                      style={{ flex: 1 }}
                      aria-label={`${Math.round(row.progress)}% complete`}
                    />
                    <Text size="xs" fw={700} style={{ minWidth: 34, textAlign: 'right' }}>
                      {Math.round(row.progress)}%
                    </Text>
                  </Group>
                )}
              </Stack>
            </UnstyledButton>
          ))}

          {more > 0 && (
            <Text size="xs" c="dimmed" mt={4} px={4}>+{more} more</Text>
          )}
        </Stack>
      )}
    </Card>
  );
}
